import {
  REMINDER_CHANNELS, isIsoDate, parseServiceInterval, reminderSettings, suggestNextService, todayIso,
  type CustomerInput, type ReminderChannel, type VehicleInput,
} from "@szerviz/core";
import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useApp, useFeature } from "../context";
import type { Vehicle, VehicleFact } from "../data";
import { ErrorText } from "./ErrorText";
import { Field, TextInput } from "./Field";
import { Help } from "./Help";

/** A date in the user's language ("2026. nov. 20."); a bare day is read at noon so no time zone moves it. */
export function useDay() {
  const { i18n } = useTranslation();
  return (iso: string | null | undefined) =>
    iso ? new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }).format(new Date(iso.length === 10 ? `${iso}T12:00:00` : iso)) : "";
}

const km = (s: string): number | null => {
  const n = Number(s.replace(/[\s.,]/g, ""));
  return s.trim() === "" || !Number.isInteger(n) || n < 0 ? null : n;
};

/** The reminder dates in the vehicle form (only when the "reminders" feature is on). */
export function VehicleReminderFields({ form, onChange }: { form: VehicleInput; onChange: (patch: Partial<VehicleInput>) => void }) {
  const { t } = useTranslation();
  const on = useFeature("reminders");
  if (!on) return null;
  return (
    <fieldset className="reminder-fields wide">
      <legend>{t("vehicle.remindersTitle")}</legend>
      <TextInput label={t("vehicle.inspectionValidUntil")} type="date" value={form.inspectionValidUntil} onChange={(v) => onChange({ inspectionValidUntil: v || null })} help="vehicle.inspectionValidUntil" />
      <TextInput label={t("vehicle.nextServiceDate")} type="date" value={form.nextServiceDate} onChange={(v) => onChange({ nextServiceDate: v || null })} help="vehicle.nextService" />
      <TextInput label={t("vehicle.nextServiceKm")} type="number" value={form.nextServiceKm} onChange={(v) => onChange({ nextServiceKm: km(v) })} />
    </fieldset>
  );
}

/**
 * "Kér emlékeztetőt": the customer's consent, per channel (off by default). Ticking it ticks
 * the channels the customer has a contact for; a ticked channel without contact is marked red.
 */
export function CustomerReminderConsent({ form, onChange, consentAt }: { form: CustomerInput; onChange: (channels: ReminderChannel[]) => void; consentAt?: string | null }) {
  const { t } = useTranslation();
  const day = useDay();
  const on = useFeature("reminders");
  if (!on) return null;
  const channels = form.reminderChannels ?? [];
  const has = { email: !!form.email?.trim(), sms: !!(form.mobile?.trim() || form.phone?.trim()) };
  const asked = channels.length > 0;
  const toggle = (c: ReminderChannel, yes: boolean) => onChange(REMINDER_CHANNELS.filter((x) => (x === c ? yes : channels.includes(x))));
  return (
    <fieldset className="reminder-consent wide">
      <legend>
        <label className="consent-main">
          <input
            type="checkbox"
            checked={asked}
            aria-label={t("customer.reminders")}
            onChange={(e) => onChange(e.target.checked ? REMINDER_CHANNELS.filter((c) => has[c]).concat(has.email || has.sms ? [] : ["email"]) : [])}
          />
          <span>{t("customer.reminders")}</span>
        </label>
        <Help id="customer.reminders" />
      </legend>
      {asked && (
        <div className="consent-channels">
          {REMINDER_CHANNELS.map((c) => (
            <label key={c} className={`consent-channel${channels.includes(c) && !has[c] ? " invalid" : ""}`}>
              <input type="checkbox" checked={channels.includes(c)} onChange={(e) => toggle(c, e.target.checked)} aria-label={t(c === "email" ? "customer.reminderEmail" : "customer.reminderSms")} />
              <span>{t(c === "email" ? "customer.reminderEmail" : "customer.reminderSms")}</span>
            </label>
          ))}
          {channels.includes("email") && !has.email && <p className="missing">{t("customer.reminderNeedsEmail")}</p>}
          {channels.includes("sms") && !has.sms && <p className="missing">{t("customer.reminderNeedsPhone")}</p>}
          {consentAt && <p className="muted small">{t("customer.reminderSince", { date: day(consentAt) })}</p>}
        </div>
      )}
    </fieldset>
  );
}

/** A ticked channel the customer has no contact for: the save button stays grey. */
export function reminderContactMissing(c: CustomerInput): boolean {
  const ch = c.reminderChannels ?? [];
  return (ch.includes("email") && !c.email?.trim()) || (ch.includes("sms") && !(c.mobile?.trim() || c.phone?.trim()));
}

/** The data sheet's service interval: a confirmed value first, else the first one. */
export function serviceIntervalOf(facts: VehicleFact[]) {
  const rows = facts.filter((f) => f.field === "serviceInterval");
  for (const f of [...rows.filter((r) => r.confirmed), ...rows]) {
    const parsed = parseServiceInterval(f.value);
    if (parsed) return parsed;
  }
  return null;
}

export interface ReminderDates {
  inspectionValidUntil: string | null;
  nextServiceDate: string | null;
  nextServiceKm: number | null;
}

/**
 * Small optional fields when a work order is closed: inspection valid until and next service,
 * the service suggested from the type data sheet's interval (from today and the work order's km).
 * The values are handed up; the closing saves them to the vehicle.
 */
export function CloseReminderDates({ vehicle, odometerKm, value, onChange }: { vehicle: Vehicle; odometerKm: number | null; value: ReminderDates; onChange: (d: ReminderDates) => void }) {
  const { t, i18n } = useTranslation();
  const { store } = useApp();
  const on = useFeature("reminders");
  const day = useDay();
  const [facts, setFacts] = useState<VehicleFact[]>([]);
  useEffect(() => {
    if (on && vehicle.typeId) void store.vehicleDataSheet(vehicle.id).then(setFacts, () => setFacts([]));
  }, [on, store, vehicle.id, vehicle.typeId]);
  if (!on) return null;
  const suggestion = suggestNextService(serviceIntervalOf(facts), todayIso(), odometerKm);
  const label = suggestion ? [suggestion.date ? day(suggestion.date) : null, suggestion.km ? `${suggestion.km.toLocaleString(i18n.language)} km` : null].filter(Boolean).join(" · ") : "";
  const same = !!suggestion && (suggestion.date ?? null) === value.nextServiceDate && (suggestion.km ?? null) === value.nextServiceKm;
  return (
    <div className="close-reminders" role="group" aria-label={t("close.reminderTitle")}>
      <h3>{t("close.reminderTitle")}</h3>
      <div className="form-grid">
        <TextInput label={t("vehicle.inspectionValidUntil")} type="date" value={value.inspectionValidUntil} onChange={(v) => onChange({ ...value, inspectionValidUntil: v || null })} help="vehicle.inspectionValidUntil" />
        <TextInput label={t("vehicle.nextServiceDate")} type="date" value={value.nextServiceDate} onChange={(v) => onChange({ ...value, nextServiceDate: v || null })} help="vehicle.nextService" />
        <TextInput label={t("vehicle.nextServiceKm")} type="number" value={value.nextServiceKm} onChange={(v) => onChange({ ...value, nextServiceKm: km(v) })} />
      </div>
      {suggestion && !same && (
        <button type="button" onClick={() => onChange({ ...value, nextServiceDate: suggestion.date ?? value.nextServiceDate, nextServiceKm: suggestion.km ?? value.nextServiceKm })}>
          {t("close.reminderSuggest", { value: label })}
        </button>
      )}
      <p className="muted small">{t("close.reminderNote")}</p>
    </div>
  );
}

export const reminderDatesOf = (v: Vehicle | null): ReminderDates => ({
  inspectionValidUntil: v?.inspectionValidUntil ?? null,
  nextServiceDate: v?.nextServiceDate ?? null,
  nextServiceKm: v?.nextServiceKm ?? null,
});

export const reminderDatesChanged = (v: Vehicle | null, d: ReminderDates) => {
  const a = reminderDatesOf(v);
  return a.inspectionValidUntil !== d.inspectionValidUntil || a.nextServiceDate !== d.nextServiceDate || a.nextServiceKm !== d.nextServiceKm;
};

/** Settings of the feature: channels (ticks), days before, and what the customer receives. */
export function ReminderSettingsPanel({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation();
  const { store, workshop, reloadWorkshop } = useApp();
  const on = useFeature("reminders");
  const day = useDay();
  const current = reminderSettings(workshop.general);
  const [email, setEmail] = useState(current.email);
  const [sms, setSms] = useState(current.sms);
  const [inspectionDays, setInspectionDays] = useState(String(current.days.inspection));
  const [serviceDays, setServiceDays] = useState(String(current.days.service));
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  if (!on) return null;
  const smsFeature = workshop.features.includes("sms");
  const daysOk = (s: string) => /^\d{1,3}$/.test(s.trim()) && Number(s) >= 1 && Number(s) <= 120;
  const valid = daysOk(inspectionDays) && daysOk(serviceDays);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaved(false);
    try {
      await store.saveGeneralSettings(workshop.id, {
        ...workshop.general,
        reminderEmail: email,
        reminderSms: sms && smsFeature,
        reminderInspectionDays: Number(inspectionDays),
        reminderServiceDays: Number(serviceDays),
      });
      await reloadWorkshop();
      setSaved(true);
    } catch (err) {
      setError(err);
    }
  };

  const phone = typeof workshop.general.workshopPhone === "string" ? workshop.general.workshopPhone.trim() : "";
  const sampleDate = day(todayIso().slice(0, 8) + "28");
  const preview = [
    t("reminder.msg.greeting", { customer: t("reminders.previewCustomer") }),
    t("reminder.msg.inspectionText", { plate: t("reminders.previewPlate"), date: sampleDate }),
    phone ? t("reminder.msg.call", { phone }) : "",
    t("reminder.msg.signature", { workshop: workshop.name }),
  ].filter(Boolean).join(" ");

  return (
    <section className="reminder-settings" aria-label={t("reminders.settingsTitle")}>
      <h2>{t("reminders.settingsTitle")}</h2>
      <p className="muted">{t("reminders.settingsIntro")}</p>
      <form className="form-grid" onSubmit={save} noValidate>
        <label className="field switch-field">
          <span className="field-label">{t("setting.reminderEmail.label")}</span>
          <span className="switch-row">
            <input type="checkbox" checked={email} disabled={!canEdit} onChange={(e) => setEmail(e.target.checked)} aria-label={t("setting.reminderEmail.label")} />
            <span>{email ? t("manual.default.on") : t("manual.default.off")}</span>
          </span>
          <small>{t("setting.reminderEmail.description")}</small>
        </label>
        <label className="field switch-field">
          <span className="field-label">{t("setting.reminderSms.label")}</span>
          <span className="switch-row">
            <input type="checkbox" checked={sms && smsFeature} disabled={!canEdit || !smsFeature} onChange={(e) => setSms(e.target.checked)} aria-label={t("setting.reminderSms.label")} />
            <span>{sms && smsFeature ? t("manual.default.on") : t("manual.default.off")}</span>
          </span>
          <small>{t("setting.reminderSms.description")}</small>
          {!smsFeature && <small className="warn-text">{t("reminders.smsNeedsFeature")}</small>}
        </label>
        <Field label={`${t("setting.reminderInspectionDays.label")} (${t("reminders.days")})`} invalid={!daysOk(inspectionDays)} help="reminders.days">
          <input inputMode="numeric" value={inspectionDays} disabled={!canEdit} onChange={(e) => setInspectionDays(e.target.value)} aria-label={t("setting.reminderInspectionDays.label")} />
        </Field>
        <Field label={`${t("setting.reminderServiceDays.label")} (${t("reminders.days")})`} invalid={!daysOk(serviceDays)} help="reminders.days">
          <input inputMode="numeric" value={serviceDays} disabled={!canEdit} onChange={(e) => setServiceDays(e.target.value)} aria-label={t("setting.reminderServiceDays.label")} />
        </Field>
        {!email && !(sms && smsFeature) && <p className="warn wide">{t("reminders.noChannel")}</p>}
        <div className="reminder-preview wide">
          <strong>{t("reminders.preview")}</strong>
          <p>{preview}</p>
        </div>
        {canEdit && (
          <div className="actions wide">
            <ErrorText error={error} />
            {saved && <span className="ok">{t("common.saved")}</span>}
            <button type="submit" className="primary" disabled={!valid}>
              {t("common.save")}
            </button>
            {!valid && <span className="missing">{t("help.reminders.days")}</span>}
          </div>
        )}
      </form>
    </section>
  );
}
